import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Sparkles, Rocket, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { StoreBadges } from "@/components/StoreBadges";
import { useLanguage } from "@/i18n/LanguageContext";
import { APPS, getApp } from "@/data/apps";
import { Seo } from "@/components/Seo";
import CifrooDemo from "@/components/CifrooDemo";
import AndroidTestSignup from "@/components/AndroidTestSignup";

const MetaChip = ({ label, value }) =>
  value ? (
    <div className="border border-white/10 px-4 py-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted2">{label}</p>
      <p className="mt-1 font-mono text-sm">{value}</p>
    </div>
  ) : null;

export default function AppPage() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const app = getApp(slug);
  if (!app) return <Navigate to="/" replace />;
  const accent = app.accent || "#9146FF";
  const family = app.family ? APPS.filter((a) => a.family === app.family && a.slug !== app.slug) : [];

  return (
    <div className="grain min-h-screen bg-void text-white">
      <Seo
        title={`${app.name} | Fabio Comelli`}
        description={app.intro[lang].slice(0, 150) + "…"}
        path={`/apps/${app.slug}`}
      />
      <Navbar />
      <main className="px-6 pt-32 md:px-12" data-testid={`app-page-${app.slug}`}>
        <div className="mx-auto max-w-7xl">
          <Link to="/#apps" data-testid="app-back-link" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted2 transition-colors duration-300 hover:text-neon">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            {t.app.back}
          </Link>

          <Reveal className="mt-12 grid grid-cols-1 items-end gap-10 md:grid-cols-12">
            <div className="md:col-span-8">
              <div className="flex items-center gap-6">
                {app.icon ? (
                  <img src={app.icon} alt={app.name} className="h-24 w-24 rounded-[26px] border border-white/15 md:h-32 md:w-32" data-testid="app-icon" />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-[26px] border border-dashed font-display text-3xl font-bold md:h-32 md:w-32" style={{ borderColor: accent + "80", color: accent }} data-testid="app-icon">
                    S&F
                  </div>
                )}
                <div>
                  {app.comingSoon && (
                    <span className="mb-3 inline-block rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest" style={{ borderColor: accent + "66", color: accent }} data-testid="app-coming-soon-badge">
                      {t.app.soonBadge}
                    </span>
                  )}
                  <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl lg:text-6xl" data-testid="app-title">
                    {app.name}
                  </h1>
                  <p className="mt-2 text-base text-muted2 md:text-lg" data-testid="app-tagline">{app.tagline[lang]}</p>
                </div>
              </div>
            </div>
            <div className="md:col-span-4">
              <StoreBadges app={app} />
            </div>
          </Reveal>

          <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3" data-testid="app-meta">
            <MetaChip label={t.app.category} value={app.category[lang]} />
            <MetaChip label={t.app.version} value={app.version} />
            <MetaChip label={t.app.size} value={app.size} />
            <MetaChip label={t.app.age} value={app.age} />
            {app.price && <MetaChip label="€" value={app.price[lang]} />}
          </Reveal>

          {!app.playUrl && !app.appleOnly && <AndroidTestSignup app={app} />}

          {app.banner && (
            <Reveal delay={0.15} className="mt-12" data-testid="app-banner">
              <img src={app.banner} alt={`${app.name} artwork`} className="w-full border border-white/10 object-cover" />
            </Reveal>
          )}

          <section className="mt-24 grid grid-cols-1 gap-12 md:grid-cols-12" data-testid="app-description">
            <Reveal className="md:col-span-4">
              <h2 className="font-display text-3xl font-bold uppercase tracking-tight">{t.app.about}</h2>
            </Reveal>
            <div className="md:col-span-8">
              <Reveal>
                <p className="text-base leading-relaxed text-muted2 md:text-lg">{app.intro[lang]}</p>
              </Reveal>
              {app.intro2 && (
                <Reveal delay={0.08}>
                  <p className="mt-6 text-base leading-relaxed text-muted2 md:text-lg">{app.intro2[lang]}</p>
                </Reveal>
              )}
              {app.details && (
                <div className="mt-8 space-y-5" data-testid="app-details">
                  {app.details[lang].map((p, i) => (
                    <Reveal key={i} delay={0.05 * i}>
                      <p className="text-sm leading-relaxed text-muted2 md:text-base">{p}</p>
                    </Reveal>
                  ))}
                </div>
              )}
              <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
                {app.features.map((f) => (
                  <Reveal key={f.title.en} className="border border-white/10 bg-surface p-8 transition-colors duration-500 hover:border-white/30">
                    <span className="mb-5 block h-1 w-10 rounded-full" style={{ backgroundColor: accent }} />
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight">{f.title[lang]}</h3>
                    <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-muted2">
                      {f.items[lang].map((item) => (
                        <li key={item} className="flex gap-2">
                          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {app.demo && <CifrooDemo />}

          {app.screenshots.length > 0 && (
            <section className="mt-24" data-testid="app-gallery">
              <Reveal>
                <h2 className="font-display text-3xl font-bold uppercase tracking-tight">{t.app.gallery}</h2>
              </Reveal>
              <Reveal delay={0.1} className="no-scrollbar -mx-6 mt-10 flex snap-x snap-mandatory gap-8 overflow-x-auto px-6 py-6 md:-mx-12 md:px-12">
                {app.screenshots.map((s, i) => (
                  <div key={s} className="relative shrink-0 snap-center">
                    <div className="pointer-events-none absolute -inset-6 rounded-full blur-3xl" style={{ backgroundColor: accent + "1F" }} />
                    <div className="relative w-52 rounded-[2.2rem] border border-white/15 bg-surface p-1.5 md:w-64">
                      <img src={s} alt={`${app.name} screenshot ${i + 1}`} loading="lazy" className="w-full rounded-[1.9rem]" />
                    </div>
                    <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-widest text-muted2">
                      {String(i + 1).padStart(2, "0")} / {String(app.screenshots.length).padStart(2, "0")}
                    </p>
                  </div>
                ))}
              </Reveal>
            </section>
          )}

          {(app.changelog || app.roadmap) && (
          <section className={`mt-24 grid grid-cols-1 gap-6 ${app.changelog && app.roadmap ? "md:grid-cols-2" : ""}`}>
            {app.changelog && (
              <Reveal className="border border-white/10 bg-surface p-10" data-testid="app-changelog">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-5 w-5" style={{ color: accent }} />
                  <h2 className="font-display text-2xl font-bold uppercase tracking-tight">{t.app.whatsNew}</h2>
                  <span className="ml-auto font-mono text-xs text-muted2">v{app.changelog.version}</span>
                </div>
                <ul className="mt-8 space-y-4 text-sm leading-relaxed text-muted2">
                  {app.changelog[lang].map((c) => (
                    <li key={c} className="flex gap-3 border-l pl-4" style={{ borderColor: accent + "55" }}>{c}</li>
                  ))}
                </ul>
              </Reveal>
            )}
            {app.roadmap && (
            <Reveal delay={0.12} className="border border-white/10 bg-surface p-10" data-testid="app-roadmap">
              <div className="flex items-center gap-3">
                <Rocket className="h-5 w-5" style={{ color: accent }} />
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight">{t.app.roadmap}</h2>
              </div>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-muted2">{t.app.roadmapSub}</p>
              <ul className="mt-8 space-y-4">
                {app.roadmap[lang].map((r, i) => (
                  <li key={r} className="flex items-center gap-4 border border-white/10 px-5 py-4 text-sm">
                    <span className="font-mono text-xs" style={{ color: accent }}>{String(i + 1).padStart(2, "0")}</span>
                    {r}
                  </li>
                ))}
              </ul>
            </Reveal>
            )}
          </section>
          )}

          {app.disclaimer && (
            <Reveal className="mt-12">
              <p className="max-w-3xl font-mono text-[11px] leading-relaxed text-muted2/70" data-testid="app-disclaimer">{app.disclaimer[lang]}</p>
            </Reveal>
          )}

          {family.length > 0 && (
            <section className="mt-24" data-testid="app-family">
              <Reveal>
                <h2 className="font-display text-3xl font-bold uppercase tracking-tight">{t.app.family}</h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted2 md:text-base">{t.app.familySub}</p>
              </Reveal>
              <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
                {family.map((f, i) => (
                  <Reveal key={f.slug} delay={0.08 * i}>
                    <Link
                      to={`/apps/${f.slug}`}
                      data-testid={`family-link-${f.slug}`}
                      className="group flex items-center gap-5 border border-white/10 bg-surface p-6 transition-colors duration-500 hover:border-white/30"
                    >
                      <img src={f.icon} alt={f.name} className="h-16 w-16 rounded-2xl border border-white/15" loading="lazy" />
                      <div className="min-w-0">
                        <h3 className="font-display text-lg font-bold uppercase tracking-tight">{f.name}</h3>
                        <p className="mt-1 truncate text-sm text-muted2">{f.tagline[lang]}</p>
                      </div>
                      <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: f.accent }} />
                    </Link>
                  </Reveal>
                ))}
              </div>
            </section>
          )}

          <Reveal className="mt-16 mb-28 flex flex-wrap items-center gap-6 border-t border-white/10 pt-10">
            <Link
              to={`/privacy/${app.slug}`}
              data-testid="app-privacy-link"
              className="inline-flex items-center gap-2 rounded-full border px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors duration-300"
              style={{ borderColor: accent + "55", color: accent }}
            >
              <ShieldCheck className="h-4 w-4" />
              {t.app.privacy}
            </Link>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
